/**
 * Storage-backed analytics bookkeeping shared by both browser surfaces:
 * daily rollup counters, milestone once-flags, and the SW-side milestone
 * queue. No SDK import here — contexts without a PostHog instance (the
 * extension service worker) use ONLY this module; the panel/page context
 * drains what accumulates.
 *
 * All operations are read-modify-write on the single `analyticsState` key.
 * Races lose at most a counter increment — acceptable for `system`-class
 * data (docs/analytics/event-spec.md §3.4). `analyticsOptOut` deliberately
 * lives outside this key so these RMWs can never clobber it.
 */
import type { StorageAdapter } from "../storage/adapter";
import type { ReflectoStorage } from "../storage-schema";
import type { ReflectoAnalytics } from "./client";

type AnalyticsState = NonNullable<ReflectoStorage["analyticsState"]>;

export const OPEN_DEBOUNCE_MS = 30 * 60 * 1000;
const QUEUE_CAP = 20;
const DAY_CAP = 7;

const dayOf = (now: number) => new Date(now).toISOString().slice(0, 10);

// Serializes analyticsState RMWs in this JS context — fire-and-forget pairs
// (bump + once-flag) otherwise clobber each other. Cross-context races stay
// lossy-tolerable by design (§3.4).
let rmwChain: Promise<void> = Promise.resolve();

function serialize<T>(op: () => Promise<T>): Promise<T | undefined> {
  const run = rmwChain.then(op).catch(() => undefined);
  rmwChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

async function update(
  adapter: StorageAdapter,
  mutate: (state: AnalyticsState) => AnalyticsState | null
): Promise<void> {
  await serialize(async () => {
    const current = (await adapter.get("analyticsState")).analyticsState ?? {};
    const next = mutate(current);
    if (next) await adapter.set({ analyticsState: next });
  });
}

/** Bump a daily rollup counter (SW-safe: storage only, no SDK). */
export async function bumpCounter(
  adapter: StorageAdapter,
  counter: string,
  by = 1,
  now = Date.now()
): Promise<void> {
  await update(adapter, (state) => {
    const day = dayOf(now);
    const dayCounters = { ...(state.dayCounters ?? {}) };
    const days = Object.keys(dayCounters).sort();
    // Cap retained days so an unopened panel can't grow storage unbounded.
    while (days.length >= DAY_CAP && !dayCounters[day]) {
      const oldest = days.shift();
      if (oldest === undefined) break;
      delete dayCounters[oldest];
    }
    const counters = { ...(dayCounters[day] ?? {}) };
    counters[counter] = (counters[counter] ?? 0) + by;
    dayCounters[day] = counters;
    return { ...state, dayCounters };
  });
}

/** Queue a milestone captured in a context with no SDK (extension SW). */
export async function queueMilestone(
  adapter: StorageAdapter,
  name: string,
  props: Record<string, unknown> = {}
): Promise<void> {
  await update(adapter, (state) => {
    const queue = [...(state.queue ?? []), { name, props }].slice(-QUEUE_CAP);
    return { ...state, queue };
  });
}

/**
 * Once-flagged variant of queueMilestone for SDK-less contexts (extension
 * SW): sets the once-flag and queues in one write, so a user-intent
 * milestone observed only in the SW still fires exactly once.
 */
export async function queueMilestoneOnce(
  adapter: StorageAdapter,
  name: string,
  props: Record<string, unknown> = {}
): Promise<void> {
  await update(adapter, (state) => {
    if (state.once?.[name]) return null;
    return {
      ...state,
      once: { ...(state.once ?? {}), [name]: true },
      queue: [...(state.queue ?? []), { name, props }].slice(-QUEUE_CAP),
    };
  });
}

/** Capture a milestone at most once per device (once-flag in storage). */
export async function captureOnce(
  adapter: StorageAdapter,
  analytics: ReflectoAnalytics,
  event: Parameters<ReflectoAnalytics["capture"]>[0],
  props: Record<string, unknown> = {}
): Promise<void> {
  const fired = await serialize(async () => {
    const state = (await adapter.get("analyticsState")).analyticsState ?? {};
    if (state.once?.[event]) return false;
    await adapter.set({
      analyticsState: { ...state, once: { ...(state.once ?? {}), [event]: true } },
    });
    return true;
  });
  if (fired) analytics.capture(event, props);
}

/**
 * Panel/page-open housekeeping: capture the debounced recurring open event,
 * flush yesterday's rollup if the day rolled, drain the SW milestone queue.
 * Call once per panel mount / page load from the SDK-bearing context.
 */
export async function onSurfaceOpened(
  adapter: StorageAdapter,
  analytics: ReflectoAnalytics,
  openEvent: "side_panel_opened" | "web_app_opened",
  openCounter: string,
  now = Date.now()
): Promise<void> {
  const result = await serialize(async () => {
    const state = (await adapter.get("analyticsState")).analyticsState ?? {};
    const today = dayOf(now);
    const next: AnalyticsState = { ...state };

    const debounced = state.lastOpenAt !== undefined && now - state.lastOpenAt < OPEN_DEBOUNCE_MS;
    if (!debounced) next.lastOpenAt = now;

    // Flush every completed (non-today) day as its own rollup event.
    const staleDays = Object.keys(state.dayCounters ?? {})
      .filter((day) => day !== today)
      .sort();
    if (staleDays.length > 0) {
      const kept = { ...(state.dayCounters ?? {}) };
      for (const day of staleDays) delete kept[day];
      next.dayCounters = kept;
    }
    const queued = state.queue ?? [];
    if (queued.length > 0) next.queue = [];

    await adapter.set({ analyticsState: next });
    return { debounced, staleDays, dayCounters: state.dayCounters, queued };
  });
  if (!result) return;

  if (!result.debounced) {
    analytics.capture(openEvent, {});
    await bumpCounter(adapter, openCounter, 1, now);
  }
  for (const day of result.staleDays) {
    const counters = result.dayCounters?.[day];
    if (counters && Object.keys(counters).length > 0) {
      analytics.capture("daily_usage_rollup", { ...counters, rollup_date: day });
    }
  }
  for (const item of result.queued) {
    analytics.capture(item.name as Parameters<ReflectoAnalytics["capture"]>[0], item.props);
  }
}

/**
 * Debounced capture for recurring-intent "open" events that are NOT the
 * primary surface anchor `onSurfaceOpened` owns (side_panel_opened /
 * web_app_opened's `lastOpenAt` + its rollup/queue-drain side effects).
 * `palette_opened` reuses the same `OPEN_DEBOUNCE_MS` window and fail-open
 * contract, but is keyed independently under `paletteLastOpenAt` — the
 * palette only opens from inside an already-open panel, so sharing the
 * surface anchor would suppress it on almost every real invocation.
 */
export async function capturePaletteOpened(
  adapter: StorageAdapter,
  analytics: ReflectoAnalytics,
  now = Date.now()
): Promise<void> {
  try {
    const state = (await adapter.get("analyticsState")).analyticsState ?? {};
    const last = state.paletteLastOpenAt;
    if (last !== undefined && now - last < OPEN_DEBOUNCE_MS) return;
    await adapter.set({ analyticsState: { ...state, paletteLastOpenAt: now } });
    analytics.capture("palette_opened", {});
  } catch {
    // fail open
  }
}
